import * as React from "react"
import * as TabsPrimitive from "@radix-ui/react-tabs"

import { cn } from "@/lib/utils"

const Tabs = TabsPrimitive.Root

// Merge a forwarded ref (callback or object, possibly null) with our own
// internal callback ref so we can both expose the DOM node to callers and
// read it ourselves for the scroll-into-view effect below.
function useMergedRef(forwardedRef) {
  const innerRef = React.useRef(null)
  const setRef = React.useCallback(
    (node) => {
      innerRef.current = node
      if (typeof forwardedRef === "function") {
        forwardedRef(node)
      } else if (forwardedRef) {
        forwardedRef.current = node
      }
    },
    [forwardedRef]
  )
  return [innerRef, setRef]
}

const TabsList = React.forwardRef(({ className, ...props }, ref) => {
  const [innerRef, setRef] = useMergedRef(ref)

  // Keep the selected trigger scrolled into view — on mount, and whenever
  // selection changes (including via keyboard arrow navigation, since
  // Radix's default "automatic" activation mode moves selection together
  // with focus). Watching the `data-state` attribute Radix already toggles
  // on each TabsTrigger avoids needing the current value threaded down
  // from callers, so every call site gets this for free.
  React.useEffect(() => {
    const list = innerRef.current
    if (!list) return

    const scrollActiveIntoView = () => {
      const active = list.querySelector('[data-state="active"]')
      if (!active) return

      // Horizontal-only nudge, computed directly against this list's own
      // scrollLeft — not scrollIntoView (issue #209). scrollIntoView walks
      // every scrollable ancestor, including the page itself: on Dashboard,
      // where this list sits partway down inside CareerDashboard, that
      // dragged the whole page down on mount. Comparing bounding rects and
      // writing `list.scrollLeft` touches nothing but this element's own
      // scroll offset, so it is structurally incapable of moving the page
      // or any other ancestor, vertically or horizontally.
      //
      // The two branches below are mutually exclusive nearest-edge checks:
      // if the active trigger is clipped on the left, pull it in from the
      // left; if clipped on the right, pull it in from the right. A trigger
      // already fully visible matches neither and scrollLeft is untouched.
      // scrollLeft's native floor of 0 makes the left branch a safe no-op
      // when the #200 fix (`[justify-content:safe_center]`) has already
      // put the row at its start-aligned reachable minimum.
      const listRect = list.getBoundingClientRect()
      const activeRect = active.getBoundingClientRect()

      if (activeRect.left < listRect.left) {
        list.scrollLeft -= listRect.left - activeRect.left
      } else if (activeRect.right > listRect.right) {
        list.scrollLeft += activeRect.right - listRect.right
      }
    }

    scrollActiveIntoView()

    const observer = new MutationObserver(scrollActiveIntoView)
    observer.observe(list, {
      attributes: true,
      attributeFilter: ["data-state"],
      subtree: true,
    })
    return () => observer.disconnect()
  }, [innerRef])

  return (
    <TabsPrimitive.List
      ref={setRef}
      className={cn(
        // Base look is unchanged. Added: cap width to the container so the
        // list itself never forces the page wide, scroll horizontally
        // within that cap, and hide the scrollbar visually while keeping
        // it operable by touch (native) and keyboard (focus + arrow keys
        // scroll a focused overflow container natively).
        //
        // justify-content: safe center (issue #200) instead of plain
        // center: a plain-centred row that overflows its scroll container
        // pushes roughly half the excess out EACH side, but `scrollLeft`
        // has a hard floor of 0 — it can only ever reveal the right-hand
        // excess, so the leading trigger(s) pushed past the left edge were
        // unreachable, not just hard to find (Development Plan's 4-tab row
        // at 390px, reported from a phone). The `safe` keyword falls back
        // to start-alignment exactly when the content overflows its
        // container, so an overflowing row now begins at its first trigger
        // and scrolls right from there, while a row that fits (desktop)
        // still centres exactly as before. Support: Chrome 93+, Safari
        // 16+, Firefox 63+ — all comfortably below this app's baseline by
        // now; an engine that somehow doesn't recognise the keyword simply
        // ignores it and falls back to plain `center`, i.e. today's
        // behaviour, never worse.
        "inline-flex min-h-11 max-w-full items-center [justify-content:safe_center] overflow-x-auto rounded-lg bg-muted p-1 text-muted-foreground",
        "[-ms-overflow-style:none] [scrollbar-width:none] [-webkit-overflow-scrolling:touch] [&::-webkit-scrollbar]:hidden",
        className
      )}
      {...props} />
  )
})
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center whitespace-nowrap min-h-[var(--nw-size-tap)] rounded-md px-3 py-2.5 text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow",
      className
    )}
    {...props} />
))
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
      className
    )}
    {...props} />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
